import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  roleHasPermissions,
  type Recommendation,
  type RecommendationCategory,
  type RecommendationEffortLevel,
  type RecommendationPriorityTier,
  type Recommendations,
  type RejectionReason,
  type BulkRecommendationOp,
} from '@lizrd/contracts';
import {
  bulkRecommendations,
  getAiStatus,
  getDomain,
  getRecommendations,
  listMembers,
  markRecommendationViewed,
  runValidation,
  updateRecommendation,
} from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Card } from '../components/ui/card';
import { CategoryIcon } from '../components/CategoryIcon';
import { Medal, Coins, Trophy, Search, Sparkles, Zap, MoreHorizontal, X } from 'lucide-react';
import { Spinner } from '../components/ui/spinner';
import { Skeleton } from '../components/ui/skeleton';
import { FilterDropdown, type FilterOption } from '../components/ui/filter-dropdown';
import { FilterChip, FiltersPopover } from '../components/ui/filters-popover';
import { Menu } from '../components/ui/menu';
import { RejectRecommendationModal } from '../components/RejectRecommendationModal';
import { Confetti } from '../components/ui/confetti';
import { useToast } from '../components/ui/toast';
import { CostOptimizationGuide } from '../components/CostOptimizationGuide';
import { CostOptimizationBoard, effectiveTier } from '../components/CostOptimizationBoard';
import { GuidanceModal } from '../components/GuidanceModal';
import { type LifecycleAction } from '../components/RecommendationLifecycle';
import { EFFORT_META, PRIORITY_META, PRIORITY_RANK, usd } from '../lib/graph-visuals';

const PRIORITY_ORDER: RecommendationPriorityTier[] = ['high', 'medium', 'low'];

/** Effort levels low→high, for the "Engineering Effort" filter (REC-011). */
const EFFORT_ORDER: RecommendationEffortLevel[] = [
  'very_low',
  'low',
  'medium',
  'high',
  'very_high',
];

/** Effort as a weight for the ROI (best value-per-effort) sort. */
const EFFORT_WEIGHT: Record<RecommendationEffortLevel, number> = {
  very_low: 1,
  low: 2,
  medium: 3,
  high: 4,
  very_high: 5,
};
/** Return-on-effort: monthly saving per unit of effort. Reliability (scale-up)
 * recs have $0 saving, so they naturally sink to the bottom of the ROI sort. */
const roiOf = (r: Recommendation): number =>
  r.estimatedMonthlySaving / (EFFORT_WEIGHT[r.effortLevel] ?? 3);
/** A "quick win": actually saves, is low-effort, and isn't low-confidence. */
const isQuickWin = (r: Recommendation): boolean =>
  r.estimatedMonthlySaving > 0 &&
  (r.effortLevel === 'very_low' || r.effortLevel === 'low') &&
  r.savingConfidence !== 'low';
import { cn } from '../lib/utils';

const CATEGORY_META: Record<RecommendationCategory, { label: string; noun: string }> = {
  orphaned: { label: 'Orphaned', noun: 'orphaned resource' },
  idle: { label: 'Idle', noun: 'idle resource' },
  rightsize: { label: 'Right-size', noun: 'over-provisioned resource' },
  modernize: { label: 'Modernize', noun: 'architecture migration' },
  storage: { label: 'Storage', noun: 'storage optimization' },
  network: { label: 'Network', noun: 'networking item' },
  reliability: { label: 'Scale up', noun: 'saturated resource' },
  commitment: { label: 'Commit & save', noun: 'steady-usage commitment' },
  schedule: { label: 'Schedule', noun: 'off-hours schedule' },
  autoscale: { label: 'Autoscale', noun: 'autoscaling candidate' },
  ai: { label: 'AI & LLM', noun: 'AI / LLM token optimization' },
};
const CATEGORY_ORDER: RecommendationCategory[] = [
  'orphaned',
  'idle',
  'rightsize',
  'modernize',
  'storage',
  'network',
  'reliability',
  'commitment',
  'schedule',
  'autoscale',
  'ai',
];

// --- Time bucketing for the Completed / Validated lists (client-side) ----------
type PeriodKey = 'week' | 'month' | 'year';
const PERIOD_LABEL: Record<PeriodKey, string> = {
  week: 'This Week',
  month: 'This Month',
  year: 'This Year',
};

/** Start instants for the current week (Mon), month, and calendar year. */
function periodStarts(now: Date) {
  const week = new Date(now);
  week.setHours(0, 0, 0, 0);
  week.setDate(week.getDate() - ((week.getDay() + 6) % 7)); // back to Monday
  return {
    week,
    month: new Date(now.getFullYear(), now.getMonth(), 1),
    year: new Date(now.getFullYear(), 0, 1),
  };
}

const TIME_LABEL: Record<PeriodKey | 'all', string> = {
  all: 'All time',
  week: 'This Week',
  month: 'This Month',
  year: 'This Year',
};

/** The date that places a card in time: when it reached its current stage. */
function activityDate(r: Recommendation): string | null {
  if (r.status === 'validated') return r.validatedAt;
  if (r.status === 'completed' || r.status === 'failed_validation') return r.executedAt;
  return r.firstSeenAt;
}

const realizedIn = (validated: Recommendation[], start: Date) =>
  validated
    .filter((v) => v.validatedAt && new Date(v.validatedAt) >= start)
    .reduce((sum, v) => sum + (v.realizedMonthlySaving ?? 0), 0);

function topSavers(validated: Recommendation[], start: Date, n = 3) {
  const byName = new Map<string, number>();
  for (const v of validated) {
    if (!v.validatedAt || new Date(v.validatedAt) < start) continue;
    const name = v.executedByName ?? 'Unknown';
    byName.set(name, (byName.get(name) ?? 0) + (v.realizedMonthlySaving ?? 0));
  }
  return [...byName.entries()]
    .map(([name, saving]) => ({ name, saving }))
    .sort((a, b) => b.saving - a.saving)
    .slice(0, n);
}

const MEDAL_COLORS = ['text-warning', 'text-muted', 'text-haze'];

/** The realized-savings hero on the Validated tab: per-period totals plus a
 * top-3 savers leaderboard (gamification), the latter gated by the env setting. */
function RealizedSavingsBox({
  validated,
  showLeaderboard,
}: {
  validated: Recommendation[];
  showLeaderboard: boolean;
}) {
  const [period, setPeriod] = useState<PeriodKey>('week');
  const [expanded, setExpanded] = useState(false);
  const starts = periodStarts(new Date());
  const totals: Record<PeriodKey, number> = {
    week: realizedIn(validated, starts.week),
    month: realizedIn(validated, starts.month),
    year: realizedIn(validated, starts.year),
  };
  const leaders = topSavers(validated, starts[period]);

  return (
    <div className="rounded-lg border border-hairline bg-surface px-4 py-2.5 shadow-sm">
      {/* One compact row: title · per-period totals · leaderboard toggle. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <div className="flex items-center gap-1.5">
          <Coins aria-hidden className="h-4 w-4 text-iris" />
          <h3 className="font-display text-sm font-bold text-bone">Realized Savings</h3>
        </div>
        <div className="flex items-center gap-4">
          {(['week', 'month', 'year'] as PeriodKey[]).map((pk) => (
            <button
              key={pk}
              onClick={() => setPeriod(pk)}
              className={cn(
                'flex items-baseline gap-1.5 rounded-sm px-1 transition-colors',
                period === pk ? 'text-bone' : 'text-muted hover:text-bone',
              )}
              title={`Realized this ${pk}`}
            >
              <span className="text-[0.6rem] font-semibold uppercase tracking-wide">
                {PERIOD_LABEL[pk]}
              </span>
              <span
                className={cn(
                  'font-display text-sm font-bold',
                  period === pk ? 'text-iris' : 'text-haze',
                )}
              >
                {usd(totals[pk])}
                <span className="text-[0.55rem] font-normal text-muted">/mo</span>
              </span>
            </button>
          ))}
        </div>
        {showLeaderboard && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="ml-auto flex items-center gap-1 text-[0.7rem] font-semibold text-haze hover:text-bone"
          >
            <Trophy aria-hidden className="h-3.5 w-3.5" /> Top savers{' '}
            <span className="text-muted">{expanded ? '▾' : '▸'}</span>
          </button>
        )}
      </div>

      {showLeaderboard && expanded && (
        <div className="mt-2.5 border-t border-hairline pt-2.5">
          <div className="mb-2 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
            Top savers · {PERIOD_LABEL[period]}
          </div>
          <div>
            {leaders.length === 0 ? (
              <p className="text-xs text-muted">
                No validated savings in this period yet — be the first on the board.
              </p>
            ) : (
              <ol className="space-y-1.5">
                {leaders.map((l, i) => (
                  <li key={l.name} className="flex items-center gap-2 text-sm">
                    <span aria-hidden className="w-5 flex-none text-center">
                      {i < 3 ? (
                        <Medal aria-hidden className={cn('inline h-4 w-4', MEDAL_COLORS[i])} />
                      ) : (
                        `${i + 1}.`
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-bone">{l.name}</span>
                    <span className="font-display font-bold text-iris">
                      {usd(l.saving)}
                      <span className="text-[0.65rem] font-normal text-muted">/mo</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Persisted collapse state for the "How it works" guide (opened from the header).
const GUIDE_STORAGE_KEY = 'lizrd.cost-optimization-guide.collapsed';

/**
 * The Cost Optimization Board (Phase 7–11): a Jira-like Kanban of the
 * recommendation lifecycle — Open → Accepted → In Validation → Validated /
 * Rejected. Compact draggable cards move between lanes (firing the matching
 * lifecycle action); a click opens the full detail/execution modal. A category
 * filter and a realized-savings + leaderboard header sit above the board.
 */
export function CostOptimizationPage() {
  const { domainId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const resource = searchParams.get('resource');
  const [category, setCategory] = useState<RecommendationCategory | 'all'>('all');
  const [effort, setEffort] = useState<RecommendationEffortLevel | 'all'>('all');
  const [priority, setPriority] = useState<RecommendationPriorityTier | 'all'>('all');
  const [owner, setOwner] = useState<'all' | 'me' | 'unassigned'>('all');
  const [sortBy, setSortBy] = useState<'priority' | 'savings' | 'roi'>('priority');
  // "Quick wins" — the easy money: low-effort, worthwhile, not low-confidence.
  const [quickWins, setQuickWins] = useState(false);
  const [timeFilter, setTimeFilter] = useState<PeriodKey | 'all'>('all');
  // Free-text search across title / resource name / external id (REC-011 follow-up:
  // the board can hold 100+ items, so a search beats scrolling).
  const [search, setSearch] = useState('');
  // The "how it works" guide — opened from the header link; collapse is persisted
  // (default-open on first visit, matching the Explorer/Settings guides).
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return localStorage.getItem(GUIDE_STORAGE_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const setGuide = (v: boolean) => {
    setGuideOpen(v);
    try {
      localStorage.setItem(GUIDE_STORAGE_KEY, v ? '0' : '1');
    } catch {
      /* private mode — the choice just won't persist */
    }
  };
  // The detail/execution modal is page-level (keyed by id) so it survives a
  // status change moving its card between lanes.
  const [activeRecId, setActiveRecId] = useState<string | null>(null);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  // Bulk selection (ACT-010): a dedicated select mode (off by default — no
  // checkboxes cluttering the cards) toggled by "Apply bulk actions".
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const clearSelection = () => setSelected(new Set());
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const qc = useQueryClient();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);
  const manualValidationRun = useAuthStore((s) => s.user?.manualValidationRun);
  // Acting on recommendations (accept/reject, narrate, guide) is gated on
  // recommendations:manage — every role except VIEWER, so engineers are included.
  const canManage = role ? roleHasPermissions(role, [Permission.RecommendationsManage]) : false;
  // "Run validation" is admin-only and only where the periodic scheduler is off
  // (staging/local) — in prod validation is continuous, so the control is hidden.
  const canRunValidation =
    !!role && roleHasPermissions(role, [Permission.OrgManage]) && manualValidationRun === true;
  const [runningValidation, setRunningValidation] = useState(false);
  async function onRunValidation() {
    setRunningValidation(true);
    try {
      const { triggered } = await runValidation();
      toast({
        title: 'Validation run started',
        description:
          triggered > 0
            ? `Syncing ${triggered} integration${triggered === 1 ? '' : 's'} — recommendations refresh shortly.`
            : 'No connected integrations to sync in this org yet.',
      });
    } catch (err) {
      toast({
        title: 'Could not start a validation run',
        description: err instanceof Error ? err.message : 'Please try again.',
        tone: 'danger',
      });
    } finally {
      setRunningValidation(false);
    }
  }

  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    enabled: !!domainId,
    // While the graph exists but recs are still being generated in the background,
    // poll quickly so they appear as soon as they're ready; otherwise settle to the
    // slow background refresh.
    refetchInterval: (query) => (query.state.data?.computing ? 4_000 : 60_000),
  });
  const aiStatus = useQuery({
    queryKey: ['ai-status', domainId],
    queryFn: () => getAiStatus(domainId),
    enabled: !!domainId,
    staleTime: 5 * 60_000,
  });
  const aiEnabled = aiStatus.data?.enabled ?? false;
  const env = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
    enabled: !!domainId,
    staleTime: 5 * 60_000,
  });
  const showLeaderboard = env.data?.showLeaderboard ?? true;

  const allItems = recs.data?.items ?? [];
  const scoped = resource ? allItems.filter((i) => i.nodeExternalId === resource) : allItems;
  const resourceName = resource ? scoped[0]?.resourceName : undefined;

  const byCategory = useMemo(() => {
    const counts = {} as Record<RecommendationCategory, number>;
    for (const i of scoped) counts[i.category] = (counts[i.category] ?? 0) + 1;
    return counts;
  }, [scoped]);
  const byEffort = useMemo(() => {
    const counts = {} as Record<RecommendationEffortLevel, number>;
    for (const i of scoped) counts[i.effortLevel] = (counts[i.effortLevel] ?? 0) + 1;
    return counts;
  }, [scoped]);
  const byPriority = useMemo(() => {
    const counts = {} as Record<RecommendationPriorityTier, number>;
    for (const i of scoped) {
      const t = effectiveTier(i);
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }, [scoped]);
  const anyFilterActive =
    category !== 'all' ||
    effort !== 'all' ||
    priority !== 'all' ||
    owner !== 'all' ||
    timeFilter !== 'all' ||
    quickWins;
  const clearFilters = () => {
    setCategory('all');
    setEffort('all');
    setPriority('all');
    setOwner('all');
    setTimeFilter('all');
    setQuickWins(false);
  };
  // The attribute filters tucked into the Filters popover (Quick wins + search are
  // their own visible controls), for the popover's active-count badge.
  const activeFilterCount =
    (category !== 'all' ? 1 : 0) +
    (priority !== 'all' ? 1 : 0) +
    (effort !== 'all' ? 1 : 0) +
    (owner !== 'all' ? 1 : 0) +
    (timeFilter !== 'all' ? 1 : 0);
  const catFiltered = category === 'all' ? scoped : scoped.filter((i) => i.category === category);
  const effFiltered =
    effort === 'all' ? catFiltered : catFiltered.filter((i) => i.effortLevel === effort);
  const prioFiltered =
    priority === 'all' ? effFiltered : effFiltered.filter((i) => effectiveTier(i) === priority);
  const timeStart = timeFilter === 'all' ? null : periodStarts(new Date())[timeFilter];
  const timeFiltered = timeStart
    ? prioFiltered.filter((i) => {
        const d = activityDate(i);
        return d != null && new Date(d) >= timeStart;
      })
    : prioFiltered;
  const ownerFiltered =
    owner === 'all'
      ? timeFiltered
      : owner === 'me'
        ? timeFiltered.filter((i) => i.assignedToId === userId)
        : timeFiltered.filter((i) => i.assignedToId === null);
  const qwFiltered = quickWins ? ownerFiltered.filter(isQuickWin) : ownerFiltered;
  const q = search.trim().toLowerCase();
  const searchFiltered = q
    ? qwFiltered.filter(
        (i) =>
          (i.title?.toLowerCase().includes(q) ?? false) ||
          (i.resourceName?.toLowerCase().includes(q) ?? false) ||
          (i.nodeExternalId?.toLowerCase().includes(q) ?? false),
      )
    : qwFiltered;
  // Client-side ordering so a manual priority override reorders too. Priority sort
  // uses effective tier then score; savings is the classic view; ROI ranks by
  // saving-per-unit-effort (best bang for the buck first).
  const visible = [...searchFiltered].sort((a, b) =>
    sortBy === 'savings'
      ? b.estimatedMonthlySaving - a.estimatedMonthlySaving
      : sortBy === 'roi'
        ? roiOf(b) - roiOf(a)
        : (PRIORITY_RANK[effectiveTier(b)] ?? 0) - (PRIORITY_RANK[effectiveTier(a)] ?? 0) ||
          b.priorityScore - a.priorityScore,
  );

  // At-a-glance board headline (whole board, independent of the filters below).
  const openItems = allItems.filter((i) => i.status === 'open');
  const recoverable = openItems.reduce((s, i) => s + i.estimatedMonthlySaving, 0);
  const inValidationCount = allItems.filter(
    (i) => i.status === 'completed' || i.status === 'failed_validation',
  ).length;
  const validatedThisMonth = realizedIn(
    allItems.filter((i) => i.status === 'validated'),
    periodStarts(new Date()).month,
  );

  const activeRec = activeRecId ? (allItems.find((i) => i.id === activeRecId) ?? null) : null;

  // Opening a card marks it "seen" (clears its "New" badge, per-user). We update
  // the list cache optimistically so the badge clears instantly, then persist
  // fire-and-forget — a failed mark just leaves it "New" a little longer.
  const viewMut = useMutation({
    mutationFn: (id: string) => markRecommendationViewed(domainId, id),
  });
  const openRec = (id: string) => {
    setActiveRecId(id);
    const current = allItems.find((i) => i.id === id);
    if (!current || current.viewed) return;
    qc.setQueryData<Recommendations>(['recommendations', domainId], (prev) =>
      prev
        ? { ...prev, items: prev.items.map((i) => (i.id === id ? { ...i, viewed: true } : i)) }
        : prev,
    );
    viewMut.mutate(id);
  };

  const act = useMutation({
    mutationFn: (vars: {
      id: string;
      action: 'accept' | 'reject' | 'complete' | 'reopen';
      rejectionReason?: RejectionReason;
      rejectionNote?: string;
    }) =>
      updateRecommendation(domainId, vars.id, {
        action: vars.action,
        rejectionReason: vars.rejectionReason,
        rejectionNote: vars.rejectionNote,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['recommendations', domainId] });
      qc.invalidateQueries({ queryKey: ['assessment', domainId] });
      qc.invalidateQueries({ queryKey: ['domain-assessments'] });
      const title = allItems.find((i) => i.id === vars.id)?.title ?? 'Recommendation';
      if (vars.action === 'accept') {
        toast({
          title: 'Recommendation accepted',
          description: `“${title}” is now tracked as accepted.`,
          tone: 'success',
        });
      } else if (vars.action === 'complete') {
        // Executing an optimization is worth celebrating: fire the overlay, close
        // the modal, and confirm with the usual toast.
        setCelebrate(true);
        setActiveRecId(null);
        toast({
          title: 'Optimization executed',
          description: `“${title}” is now awaiting validation — Lizrd will confirm the saving over the next syncs.`,
          tone: 'success',
        });
      } else if (vars.action === 'reopen') {
        toast({
          title: 'Recommendation re-opened',
          description: `“${title}” is back in your open list.`,
          tone: 'info',
        });
      } else if (vars.action === 'reject') {
        toast({
          title: 'Recommendation rejected',
          description: `“${title}” moved to Rejected — thanks, the reason helps us improve.`,
          tone: 'info',
        });
      }
    },
  });

  // Bulk actions (ACT-010): candidate assignees are members with env access, and
  // one operation is applied to every selected id (best-effort, partial results).
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers, enabled: canManage });
  const eligibleMembers = (members.data ?? []).filter(
    (m) => m.role === 'ADMINISTRATOR' || m.role === 'MANAGER' || m.domainIds.includes(domainId),
  );
  const bulkMut = useMutation({
    mutationFn: (op: BulkRecommendationOp) =>
      bulkRecommendations(domainId, { ids: [...selected], op }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['recommendations', domainId] });
      qc.invalidateQueries({ queryKey: ['assessment', domainId] });
      qc.invalidateQueries({ queryKey: ['domain-assessments'] });
      const ok = res.results.filter((r) => r.ok).length;
      const failed = res.results.length - ok;
      toast({
        title: `${ok} updated${failed ? `, ${failed} skipped` : ''}`,
        description: failed
          ? 'Some items could not be changed (e.g. an invalid state).'
          : undefined,
        tone: failed ? 'info' : 'success',
      });
      clearSelection();
      setBulkRejectOpen(false);
    },
  });

  // The one place lifecycle transitions are dispatched (from cards and the modal
  // alike): reject asks for confirmation; every other move runs immediately.
  const handleAction = (id: string, action: LifecycleAction) => {
    if (action === 'reject') setRejectTargetId(id);
    else act.mutate({ id, action });
  };
  const rejectTarget = rejectTargetId
    ? (allItems.find((i) => i.id === rejectTargetId) ?? null)
    : null;

  if (recs.isLoading) {
    return (
      <div className="space-y-4" role="status" aria-label="Analyzing your domain">
        <CostOptimizationGuide open={guideOpen} onClose={() => setGuide(false)} />
        <Skeleton className="mx-auto h-14 w-full max-w-2xl rounded-lg" />
        <Skeleton className="h-5 w-56" />
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-20 rounded-full" />
          ))}
        </div>
        <div className="overflow-x-auto">
          <div className="grid min-w-[1040px] grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="space-y-2 rounded-lg border border-hairline bg-surface-2/70 p-2"
              >
                <Skeleton className="h-6 w-full rounded-md" />
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} className="h-16 w-full rounded-md" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-tour="cost-board">
      <CostOptimizationGuide open={guideOpen} onClose={() => setGuide(false)} />

      {/* Resource filter banner */}
      {resource && (
        <div className="flex items-center gap-2 rounded-md border border-iris/25 bg-iris/5 px-3 py-2 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <Search aria-hidden className="h-3.5 w-3.5" />
            Showing optimizations for
          </span>
          <span className="font-semibold text-bone">{resourceName ?? resource}</span>
          <button
            onClick={() => {
              searchParams.delete('resource');
              setSearchParams(searchParams, { replace: true });
            }}
            className="ml-auto font-semibold text-viridian hover:underline"
          >
            ← all optimizations
          </button>
        </div>
      )}

      {allItems.length === 0 ? (
        recs.data?.computing ? (
          <Card className="grid place-items-center p-10 text-center text-sm text-muted">
            <div>
              <div className="mb-3 flex justify-center" aria-hidden>
                <Spinner className="h-6 w-6" />
              </div>
              Generating your recommendations… this can take a moment on a large account. They'll
              appear here as soon as they're ready.
            </div>
          </Card>
        ) : (
          <Card className="grid place-items-center p-10 text-center text-sm text-muted">
            <div>
              <div className="mb-2 flex justify-center" aria-hidden>
                <Sparkles className="h-8 w-8 text-muted" />
              </div>
              No optimizations found — your domain looks lean. They refresh on each sync.
            </div>
          </Card>
        )
      ) : (
        <>
          {/* Realized Savings — standard centered width, aligned with the guide above. */}
          {(recs.data?.validatedCount ?? 0) > 0 && (
            <RealizedSavingsBox
              validated={allItems.filter((i) => i.status === 'validated')}
              showLeaderboard={showLeaderboard}
            />
          )}

          {/* The board sits in the content column; the board itself scrolls
              horizontally when its five lifecycle columns exceed the width. */}
          <div>
            <div className="space-y-4">
              {/* Header: title + stats on the left, a consolidated control cluster on
                  the right (search · Quick wins · Filters popover · Sort · overflow). */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  {/* Section heading at the app's standard section size (text-lg,
                      like Settings' section headings) — not the tiny widget-label
                      size, and named for the value rather than echoing the tab. */}
                  <h2 className="font-display text-lg font-bold text-bone">
                    Optimization Opportunities
                  </h2>
                  {/* Slim at-a-glance headline so landing straight on the board still
                      orients you: what's open, what it's worth, what's in flight. */}
                  <p className="mt-1 text-xs text-muted">
                    <span className="font-semibold text-bone">{openItems.length}</span> open
                    {' · '}
                    <span className="font-semibold text-iris">{usd(recoverable)}/mo</span>{' '}
                    recoverable
                    {inValidationCount > 0 && (
                      <>
                        {' · '}
                        <span className="font-semibold text-bone">{inValidationCount}</span> in
                        validation
                      </>
                    )}
                    {validatedThisMonth > 0 && (
                      <>
                        {' · '}
                        <span className="font-semibold text-viridian">
                          {usd(validatedThisMonth)}/mo
                        </span>{' '}
                        validated this month
                      </>
                    )}
                    {!guideOpen && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={() => setGuide(true)}
                          className="font-semibold text-muted underline-offset-2 transition-colors hover:text-haze hover:underline"
                        >
                          How it works
                        </button>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Search — the board can hold 100+ items, so this beats scrolling. */}
                  <div className="relative">
                    <Search
                      aria-hidden
                      className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted"
                    />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search"
                      aria-label="Search optimizations"
                      className="h-7 w-40 rounded-md border border-border bg-surface pl-8 pr-6 text-xs text-bone placeholder:text-muted focus:border-iris focus:outline-none focus:ring-2 focus:ring-iris-dim"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        aria-label="Clear search"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted transition-colors hover:text-bone"
                      >
                        <X aria-hidden className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Quick wins — the one shortcut worth keeping visible. */}
                  <button
                    type="button"
                    onClick={() => setQuickWins((v) => !v)}
                    title="Low-effort, worthwhile, high-confidence optimizations — the easy money"
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[0.7rem] font-semibold transition-colors',
                      quickWins
                        ? 'border-transparent bg-iris text-on-iris'
                        : 'border-hairline text-haze hover:border-border-strong hover:text-bone',
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Zap aria-hidden className="h-3.5 w-3.5" />
                      Quick wins
                    </span>
                  </button>

                  {/* The five attribute filters, consolidated behind one control. */}
                  <FiltersPopover count={activeFilterCount} onClear={clearFilters}>
                    <FilterDropdown
                      label="Type"
                      value={category}
                      defaultValue="all"
                      onChange={setCategory}
                      options={
                        [
                          { value: 'all', label: 'All', count: scoped.length },
                          ...CATEGORY_ORDER.filter((c) => byCategory[c]).map((c) => ({
                            value: c,
                            label: (
                              <span className="inline-flex items-center gap-1.5">
                                <CategoryIcon category={c} className="h-3.5 w-3.5" />
                                {CATEGORY_META[c].label}
                              </span>
                            ),
                            count: byCategory[c],
                          })),
                        ] as FilterOption<RecommendationCategory | 'all'>[]
                      }
                    />
                    <FilterDropdown
                      label="Priority"
                      value={priority}
                      defaultValue="all"
                      onChange={setPriority}
                      options={
                        [
                          { value: 'all', label: 'All' },
                          ...PRIORITY_ORDER.filter((t) => byPriority[t]).map((t) => ({
                            value: t,
                            label: PRIORITY_META[t]?.label ?? t,
                            count: byPriority[t],
                          })),
                        ] as FilterOption<RecommendationPriorityTier | 'all'>[]
                      }
                    />
                    <FilterDropdown
                      label="Effort"
                      value={effort}
                      defaultValue="all"
                      onChange={setEffort}
                      options={
                        [
                          { value: 'all', label: 'All' },
                          ...EFFORT_ORDER.filter((e) => byEffort[e]).map((e) => ({
                            value: e,
                            label: EFFORT_META[e]?.label ?? e,
                            count: byEffort[e],
                          })),
                        ] as FilterOption<RecommendationEffortLevel | 'all'>[]
                      }
                    />
                    <FilterDropdown
                      label="Owner"
                      value={owner}
                      defaultValue="all"
                      onChange={setOwner}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'me', label: 'Assigned to me' },
                        { value: 'unassigned', label: 'Unassigned' },
                      ]}
                    />
                    <FilterDropdown
                      label="Time"
                      value={timeFilter}
                      defaultValue="all"
                      onChange={setTimeFilter}
                      options={(['all', 'week', 'month', 'year'] as const).map((t) => ({
                        value: t,
                        label: TIME_LABEL[t],
                      }))}
                    />
                  </FiltersPopover>

                  <FilterDropdown
                    label="Sort"
                    value={sortBy}
                    onChange={setSortBy}
                    options={[
                      { value: 'priority', label: 'Priority' },
                      { value: 'savings', label: 'Savings' },
                      { value: 'roi', label: 'Best ROI' },
                    ]}
                  />

                  {/* Overflow: the rarely-used run-validation + bulk-actions controls. */}
                  {(canRunValidation || canManage) && (
                    <Menu
                      ariaLabel="More actions"
                      trigger={<MoreHorizontal aria-hidden className="h-4 w-4" />}
                      items={[
                        ...(canRunValidation
                          ? [
                              {
                                label: runningValidation ? 'Running…' : 'Run validation',
                                onClick: () => {
                                  if (!runningValidation) void onRunValidation();
                                },
                              },
                            ]
                          : []),
                        ...(canManage
                          ? [
                              {
                                label: selectMode ? 'Exit bulk actions' : 'Apply bulk actions',
                                onClick: () =>
                                  selectMode ? exitSelectMode() : setSelectMode(true),
                              },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </div>
              </div>

              {/* Active filters as removable chips — surfaces what's tucked in the popover. */}
              {anyFilterActive && (
                <div className="flex flex-wrap items-center gap-2">
                  {category !== 'all' && (
                    <FilterChip
                      label={`Type: ${CATEGORY_META[category].label}`}
                      onClear={() => setCategory('all')}
                    />
                  )}
                  {priority !== 'all' && (
                    <FilterChip
                      label={`Priority: ${PRIORITY_META[priority]?.label ?? priority}`}
                      onClear={() => setPriority('all')}
                    />
                  )}
                  {effort !== 'all' && (
                    <FilterChip
                      label={`Effort: ${EFFORT_META[effort]?.label ?? effort}`}
                      onClear={() => setEffort('all')}
                    />
                  )}
                  {owner !== 'all' && (
                    <FilterChip
                      label={`Owner: ${owner === 'me' ? 'Assigned to me' : 'Unassigned'}`}
                      onClear={() => setOwner('all')}
                    />
                  )}
                  {timeFilter !== 'all' && (
                    <FilterChip
                      label={`Time: ${TIME_LABEL[timeFilter]}`}
                      onClear={() => setTimeFilter('all')}
                    />
                  )}
                  {quickWins && (
                    <FilterChip label="Quick wins" onClear={() => setQuickWins(false)} />
                  )}
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-[0.7rem] font-semibold text-muted hover:text-haze"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {canManage && selectMode && selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-iris/40 bg-iris/5 px-3 py-2 text-xs">
                  <span className="font-semibold text-bone">{selected.size} selected</span>
                  <button
                    disabled={bulkMut.isPending}
                    onClick={() => bulkMut.mutate({ type: 'status', action: 'accept' })}
                    className="rounded-sm border border-hairline px-2 py-0.5 font-semibold text-haze hover:text-bone disabled:opacity-50"
                  >
                    Accept
                  </button>
                  <button
                    disabled={bulkMut.isPending}
                    onClick={() => setBulkRejectOpen(true)}
                    className="rounded-sm border border-hairline px-2 py-0.5 font-semibold text-haze hover:text-bone disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <select
                    disabled={bulkMut.isPending}
                    defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value;
                      e.target.value = '';
                      if (v === '') return;
                      bulkMut.mutate({
                        type: 'assign',
                        assignedToId: v === '__unassign' ? null : v,
                      });
                    }}
                    className="rounded-sm border border-hairline bg-surface-2 px-1.5 py-0.5 text-haze"
                  >
                    <option value="">Assign to…</option>
                    <option value="__unassign">Unassign</option>
                    {eligibleMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name ?? m.email}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={clearSelection}
                    className="ml-auto rounded-sm px-2 py-0.5 font-semibold text-muted hover:text-haze"
                  >
                    Clear
                  </button>
                </div>
              )}

              <CostOptimizationBoard
                items={visible}
                canManage={canManage}
                pendingId={act.isPending ? (act.variables?.id ?? null) : null}
                onOpen={openRec}
                onAction={handleAction}
                selectMode={selectMode}
                selected={selected}
                onToggleSelect={toggleSelect}
              />
            </div>
          </div>
        </>
      )}

      {activeRec && (
        <GuidanceModal
          rec={activeRec}
          envId={domainId}
          canManage={canManage}
          aiEnabled={aiEnabled}
          actionPending={act.isPending && act.variables?.id === activeRec.id}
          onAction={(action) => handleAction(activeRec.id, action)}
          onClose={() => setActiveRecId(null)}
        />
      )}

      <RejectRecommendationModal
        open={!!rejectTarget}
        title={rejectTarget?.title ?? null}
        pending={act.isPending}
        onCancel={() => setRejectTargetId(null)}
        onConfirm={(reason, note) => {
          if (rejectTarget)
            act.mutate({
              id: rejectTarget.id,
              action: 'reject',
              rejectionReason: reason,
              rejectionNote: note || undefined,
            });
          setRejectTargetId(null);
        }}
      />

      <RejectRecommendationModal
        open={bulkRejectOpen}
        title={`${selected.size} recommendation${selected.size === 1 ? '' : 's'}`}
        pending={bulkMut.isPending}
        onCancel={() => setBulkRejectOpen(false)}
        onConfirm={(reason, note) =>
          bulkMut.mutate({
            type: 'status',
            action: 'reject',
            rejectionReason: reason,
            rejectionNote: note || undefined,
          })
        }
      />

      <Confetti active={celebrate} onDone={() => setCelebrate(false)} />
    </div>
  );
}
